import { useEffect, useRef, useState } from 'react';
import { MOCK } from '../lib/supabase.js';
import * as db from '../lib/data.js';
import { Modal } from './Modals.jsx';
import { useLeaveGuard } from '../lib/leaveguard.js';
import { routeUrl } from '../lib/route.js';
import { STATUS_LABEL, fmtUsd, thaiOr } from '../lib/materials.js';

// หาวัสดุด้วย AI — หน้าต่าง "ดูก่อนลงคิว" (สัญญา: docs/ai-materials-plan.md)
//   เปิดมา = ถามเซิร์ฟเวอร์แบบ dry_run ก่อนเสมอ (ไม่เขียน ไม่เริ่มงาน) → โชว์จำนวนรุ่น/ค่าใช้จ่าย → คนกดลงคิว → ผลต่อรุ่น
//   ที่มา: key เดียวทั้งบ้าน + auto-reload — ของเดิมคลิกเดียวลงคิวทั้งแคตตาล็อกได้
//
// ⚠ กับดักที่กันไว้ (เคยทำข้อมูลหายในแอพนี้จริง):
//   · ไม่มี <form> เลย + ทุกปุ่ม type="button" → Enter ในช่องกรอกไม่กดบันทึก/ไม่ปิดหน้าต่าง
//   · ลากเลือกข้อความแล้วปล่อยนอกกรอบ = ไม่ปิด (Modal เช็คทั้ง mousedown และ click)
//   · ทุกการเขียนหลัง await ใช้ setX(prev => …) + เช็คว่าหน้าต่างยังเปิดอยู่ · คำตอบ dry_run เก่าที่มาช้าถูกทิ้ง
//   · กันกดซ้ำด้วย ref (state ยังไม่ทันอัปเดตตอนดับเบิลคลิก = ลงคิว/จ่ายเงินซ้ำ)
//   · กำลังส่ง → ปิดไม่ได้ทันที (รวมปุ่มย้อนกลับของเบราว์เซอร์ผ่าน useLeaveGuard)
//
// ⚠ หน้าต่างนี้ไม่เขียนค่าวัสดุลงสินค้าเด็ดขาด — ทุกการเขียนอยู่ที่หน้าตรวจผล /materials ที่เดียว (ตัดสินใจ 1 พ.ย. 69)
//   เหตุ: งานลงคิวขยายเป็นทั้งรุ่น รายชื่อ "SKU ใหม่" จึงรวมสีที่คนจงใจไม่ติ๊กและค่าที่ตรวจมือแล้ว
//   ที่นี่ไม่มีค่าเดิม ไม่มีคำเตือนสีต่างกัน — แถวผลจึงมีแค่ลิงก์ไปแถวนั้นที่หน้าตรวจผล

// ตารางพร้อมไหม → ซ่อนปุ่มถ้ายังไม่รัน deploy-38 (ไม่มี error ดิบ · รันเมื่อไหร่ปุ่มโผล่เอง)
export function useMaterialsReady(canAi) {
  const [ready, setReady] = useState(MOCK && !!canAi);
  useEffect(() => {
    if (MOCK || !canAi) { setReady(MOCK && !!canAi); return undefined; }
    let alive = true;
    db.materialsReady().then(v => { if (alive) setReady(v); });
    return () => { alive = false; };
  }, [canAi]);
  return ready;
}

// ── MOCK: ไม่มีเซิร์ฟเวอร์ — ตอบครบทุกแบบเพื่อดูหน้าตา ─────────────────────────
const MOCK_ACTIONS = [
  { action: 'queued' },
  { action: 'merged' },
  { action: 'pending', status: 'researching', research_id: 'r-ing' },
  { action: 'pending', status: 'draft', research_id: 'r-ok-1' },
  { action: 'exists', status: 'approved', research_id: 'r-appr', skus_not_in_result: ['GDN-093-GN', 'GDN-093-RD'] },
  { action: 'exists', status: 'not_found', research_id: 'r-notfound', skus_not_in_result: ['MOCK-NEW-3'] },
  { action: 'exists', status: 'rejected', research_id: 'r-rej' },
];
const mockDry = (job) => {
  const n = job.ids.length;
  const models = Math.max(1, Math.ceil(n * 0.6));
  const fresh = job.force ? models : Math.max(1, models - Math.floor(n / 3));
  return {
    dry_run: true, selected_rows: n, skus_total: n + Math.ceil(n / 2), models_total: models,
    models_new: fresh, models_existing: models - fresh, models_active: 0,
    not_found_ids: [], skipped_rows: n > 3 ? [{ id: 'x', sku: 'STICKER2', reason: 'skip_skus' }, { id: 'y', sku: 'MOCK-RX-1', reason: 'bad_regex' }] : [],
    unregistered_brands: n > 3 ? ['ตัวอย่างแบรนด์ใหม่'] : [],
    regex_errors: n > 3 ? [{ brand: 'ตัวอย่างแบรนด์กติกาเสีย', pattern: '([A-Z' }] : [],
    per_click_max: 50, paused: false, paused_reason: null, daily: { spent_usd: 12.4, max_usd: 50 },
    est_usd_low: fresh * 0.55, est_usd_high: fresh * 1.1,
  };
};
const mockSend = (job) => {
  const results = job.ids.map((id, i) => ({
    brand: 'MOCK', model: `MOCK-${i + 1}`, research_id: `mock-${id}`,
    ...(job.force ? { action: 'queued' } : MOCK_ACTIONS[i % MOCK_ACTIONS.length]),
  }));
  return { results, queued: results.filter(r => r.action === 'queued').length, not_found_ids: [], skipped_rows: [], unregistered_brands: [], regex_errors: [] };
};

const ACTION_TEXT = {
  queued: 'ลงคิวแล้ว',
  merged: 'รวมเข้างานเดิมที่ยังรอคิว',
  pending: 'รุ่นนี้กำลังค้นอยู่ — SKU ใหม่รอไว้ ใช้ผลได้หลังยืนยัน',
  pending_draft: 'มีผลรอคนตรวจอยู่แล้ว — ฝากช่วยดูที่หน้าตรวจผล',
};
const actionText = (r) => (r.action === 'pending' && r.status === 'draft' ? ACTION_TEXT.pending_draft : ACTION_TEXT[r.action] ?? 'รับเรื่องแล้ว');
const REASON_TEXT = {
  no_sku: 'ยังไม่มีรหัสสินค้า',
  no_brand: 'ยังไม่ระบุแบรนด์',
  skip_sku: 'ไม่ใช่สินค้า ไม่ต้องค้น',
  skip_skus: 'ไม่ใช่สินค้า ไม่ต้องค้น',
  bad_regex: 'กติกาจัดรุ่นของแบรนด์นี้ใช้ไม่ได้',
};
const reasonText = (r) => thaiOr(r, REASON_TEXT[r] ?? 'ข้ามรายการนี้');
const rowKey = (r) => `${r.brand}|${r.model}`;
const num = (v) => Number(v ?? 0) || 0;
const LATE_SHOW = 8;
// ลิงก์ไปแถวนั้นที่หน้าตรวจผล — บอกว่าไปทำอะไรได้ที่นั่น ไม่ใช่แค่ "ดู"
const reviewLinkText = (r, lateN) => (r.action === 'pending' ? 'ไปตรวจผลนี้ →'
  : r.status === 'approved' && lateN ? `เลือก SKU ที่จะใช้ผลนี้ (${lateN}) →`
  : r.status === 'not_found' ? 'พิมพ์ค่าเองที่หน้าตรวจผล →'
  : 'ดูผลนี้ที่หน้าตรวจผล →');

function SkuList({ list }) {
  if (!list?.length) return null;
  return (
    <span className="men-skus">
      {list.slice(0, LATE_SHOW).join(' · ')}{list.length > LATE_SHOW ? ` · และอีก ${list.length - LATE_SHOW} รายการ` : ''}
    </span>
  );
}

// รายการที่ข้าม + แบรนด์ที่กติกาจัดรุ่นเสีย — ใช้ทั้งหน้าดูก่อนลงคิวและหน้าผล
function SkipNotes({ res }) {
  return (
    <>
      {!!res?.regex_errors?.length && (
        <div className="dossier-warn">
          ยังจัดรุ่นให้แบรนด์นี้ไม่ได้: {res.regex_errors.map(x => x.brand).join(' · ')} — กติกาจัดรุ่นในทะเบียนแบรนด์ใช้ไม่ได้
          จึงข้ามรายการของแบรนด์นี้ไว้ก่อน (ไม่แตกเป็นงานรายสี จะได้ไม่เสียค่าค้นซ้ำ) ฝากแจ้งแอดมินให้ช่วยแก้
        </div>
      )}
      {!!res?.skipped_rows?.length && (
        <div className="men-note"><b>ข้าม {res.skipped_rows.length} รายการ</b>
          <ul>{res.skipped_rows.slice(0, LATE_SHOW).map((s, i) => <li key={s.id ?? i}>{s.sku || 'ไม่มีรหัส'} · {reasonText(s.reason)}</li>)}</ul>
          {res.skipped_rows.length > LATE_SHOW && <span className="fhint">และอีก {res.skipped_rows.length - LATE_SHOW} รายการ</span>}
        </div>
      )}
      {!!res?.not_found_ids?.length && <div className="men-note">หาใบสินค้าไม่เจอ {res.not_found_ids.length} รายการ — อาจถูกลบไปแล้ว</div>}
    </>
  );
}

// ids = classification id ที่เลือก · force = ค้นใหม่แม้เคยยืนยัน · beforeEnqueue = ทำก่อนลงคิวจริง (↻ = ไม่ใช้ผลเดิมก่อน)
// notice = ข้อความแจ้งเพิ่ม (เช่น บรรทัดที่ยังไม่มีใบสินค้า) · onDone(res) = ลงคิวสำเร็จ
// onOpenReview(id) = เปิดหน้าตรวจผลที่แถว id (ไม่ส่ง id = เปิดหน้าเฉย ๆ) · onReviewPage = เปิดจากหน้าตรวจผลอยู่แล้ว ไม่ต้องมีปุ่มไปหน้าตรวจผล
export default function MaterialEnqueueModal({
  ids, force = false, docDate = null, beforeEnqueue = null, title = 'หาวัสดุด้วย AI', notice = null,
  onClose, onDone, onOpenReview, onReviewPage = false,
}) {
  const [job, setJob] = useState({ ids, force, docDate, beforeEnqueue, label: null });
  const [step, setStep] = useState('checking');   // checking | confirm | sending | done | notready | error
  const [dry, setDry] = useState(null);
  const [results, setResults] = useState(null);   // ผลต่อรุ่น (ค้นใหม่จากแถวในนี้ = อัปเดตแถวเดิม)
  const [summary, setSummary] = useState(null);   // not_found_ids / skipped_rows / unregistered_brands ของรอบล่าสุด
  const [err, setErr] = useState(null);
  const [rowSt, setRowSt] = useState({});         // ต่อรุ่น: { busy, msg } — ปุ่มค้นใหม่ในแถวผล
  const alive = useRef(true);
  const reqRef = useRef(0);
  const busyRef = useRef(false);
  const beforeDone = useRef(false);               // ไม่ใช้ผลเดิมไปแล้ว → ลองลงคิวใหม่ห้ามทำซ้ำ (แถวเป็น rejected แล้วจะ error)
  const dryOk = useRef(false);                    // ดูก่อนลงคิวเคยผ่านแล้ว = ฟังก์ชันมีอยู่จริง · ส่งไม่ถึงหลังจากนี้คือเชื่อมต่อไม่ได้ ไม่ใช่ยังตั้งค่าไม่เสร็จ

  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  const patchRow = (key, patch) => setRowSt(prev => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  const rowBusy = Object.values(rowSt).some(s => s?.busy);
  const sending = step === 'sending' || rowBusy;

  useLeaveGuard(sending ? 'กำลังทำรายการหาวัสดุอยู่ — ออกตอนนี้อาจไม่ครบ ออกเลยไหม?' : null);

  const safeClose = () => {
    if (sending) return;                           // ปุ่มขึ้น "กำลัง…" อยู่แล้ว · ปิดกลางทาง = ผลหายไม่มีใครเห็น
    onClose();
  };

  // ── ดูก่อนลงคิว ──
  useEffect(() => {
    const my = ++reqRef.current;
    setStep('checking'); setErr(null); setDry(null);
    (async () => {
      try {
        const d = MOCK ? mockDry(job) : await db.enqueueMaterials(job.ids, { force: job.force, dryRun: true, docDate: job.docDate });
        if (!alive.current || my !== reqRef.current) return;
        dryOk.current = true;
        setDry(d); setStep('confirm');
      } catch (e) {
        if (!alive.current || my !== reqRef.current) return;
        if (e?.fetchFailed && dryOk.current) { setErr(db.MAT_UNREACHABLE); setStep('error'); return; }
        if (e?.notReady) { setStep('notready'); return; }
        setErr(e?.message ?? 'ตรวจจำนวนรุ่นไม่สำเร็จ — ฝากลองใหม่อีกครั้ง'); setStep('error');
      }
    })();
    return undefined;
  }, [job]);

  // ── ลงคิวจริง ──
  const send = async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setStep('sending'); setErr(null);
    try {
      if (job.beforeEnqueue && !beforeDone.current) {
        await job.beforeEnqueue();
        beforeDone.current = true;
      }
      const res = MOCK ? mockSend(job) : await db.enqueueMaterials(job.ids, { force: job.force, docDate: job.docDate });
      if (!alive.current) return;
      const fresh = res?.results ?? [];
      setResults(prev => {
        if (!prev) return fresh;
        const byKey = new Map(fresh.map(r => [rowKey(r), r]));
        const kept = prev.map(r => byKey.get(rowKey(r)) ?? r);
        const seen = new Set(prev.map(rowKey));
        return [...kept, ...fresh.filter(r => !seen.has(rowKey(r)))];
      });
      setRowSt(prev => {
        const n = { ...prev };
        for (const r of fresh) delete n[rowKey(r)];
        return n;
      });
      setSummary(res);
      setStep('done');
      onDone?.(res);
    } catch (e) {
      if (!alive.current) return;
      // ไม่ใช้ผลเดิมไปแล้วห้ามทิ้งคนไว้หน้า "ยังไม่พร้อม" ที่มีแต่ปุ่มปิด — ต้องลองลงคิวซ้ำได้ และรู้ว่าผลเดิมถูกบันทึกแล้ว
      if (e?.notReady && !e?.fetchFailed && !beforeDone.current) { setStep('notready'); return; }
      // มีคนลงคิวไปก่อนหน้าจนเกินเพดาน → กลับหน้าดูก่อนลงคิว บอกตรง ๆ ไม่มีปุ่มลงคิว
      if (e?.tooMany) setDry(d => ({ ...d, ...e.tooMany }));
      // ถึงขั้นนี้ดูก่อนลงคิวผ่านแล้ว ส่งไม่ถึง = เชื่อมต่อไม่ได้ ไม่ใช่ระบบยังตั้งค่าไม่เสร็จ
      const msg = e?.fetchFailed ? db.MAT_UNREACHABLE : e?.message ?? 'ลงคิวไม่สำเร็จ — ฝากลองใหม่อีกครั้ง';
      setErr(beforeDone.current ? `${msg} · ผลเดิมถูกบันทึกว่า “ไม่ใช้” ไปแล้ว ฝากกด “ลองลงคิวอีกครั้ง” ได้เลย — ถ้าปิดหน้าต่างนี้ไปก่อน ยังกด ↻ ค้นใหม่ ที่แถวเดิมได้` : msg);
      setStep('confirm');
    } finally { busyRef.current = false; }
  };

  // ── ปุ่มต่อรุ่นในหน้าผล ──
  // ค้นใหม่จากแถวผล: ค้นทั้งรุ่น (SKU เดิม + ที่รอ) ตามสัญญา ↻ = ไม่ใช้ผลเดิมก่อน แล้วลงคิวแบบ force · ดูก่อนลงคิวเหมือนเดิม
  const researchAgain = async (r) => {
    const key = rowKey(r);
    if (busyRef.current) return;
    busyRef.current = true;
    patchRow(key, { busy: true, msg: null });
    try {
      let nextIds = [`mock-${r.model}`];
      if (!MOCK) {
        const row = await db.getMaterialResearch(r.research_id);
        const skus = [...new Set([...(row?.skus ?? []), ...(row?.late_skus ?? []), ...(r.skus_not_in_result ?? [])])];
        const cards = await db.listCardsBySkus(skus);
        nextIds = cards.map(c => c.id);
      }
      if (!alive.current) return;
      if (!nextIds.length) { patchRow(key, { msg: 'หาใบสินค้าของรุ่นนี้ไม่เจอ — อาจเปลี่ยนรหัสไปแล้ว' }); return; }
      // แถวที่ยืนยันแล้ว/ไม่ใช้แล้ว/ขัดข้อง ไม่ถือดัชนี active — ค้นใหม่ได้เลยไม่ต้องไม่ใช้ก่อน (แถวขัดข้องจะได้เก็บข้อความเดิมไว้)
      const needsReject = r.status && !['approved', 'rejected', 'error'].includes(r.status);
      beforeDone.current = false;
      setJob({
        ids: nextIds, force: true, docDate: job.docDate, label: r.model,
        beforeEnqueue: needsReject && !MOCK ? () => db.rejectMaterial(r.research_id, 'ค้นใหม่') : null,
      });
    } catch (e) {
      if (alive.current) patchRow(key, { msg: e?.message ?? 'เตรียมค้นใหม่ไม่สำเร็จ — ฝากลองใหม่อีกครั้ง' });
    } finally {
      busyRef.current = false;
      if (alive.current) patchRow(key, { busy: false });
    }
  };

  // ยกเลิกหน้าดูก่อนลงคิว: ถ้ามีผลอยู่แล้ว (กำลังค้นใหม่จากแถวผล) กลับไปหน้าผล ไม่งั้นปิด
  const backFromConfirm = () => { if (results) { setStep('done'); setErr(null); } else onClose(); };

  // ไปแถวนั้นที่หน้าตรวจผล — เป็นลิงก์จริง คลิกขวาเปิดแท็บใหม่ได้ · ไม่มีตัวนำทาง (ไม่ส่ง onOpenReview) ก็เปิดหน้าได้เอง
  const openRow = (e, id) => {
    if (!onOpenReview || e.metaKey || e.ctrlKey || e.shiftKey) return;
    e.preventDefault();
    if (sending) return;
    onClose();
    onOpenReview(id);
  };

  const overCap = dry && num(dry.per_click_max) > 0 && num(dry.models_new) > num(dry.per_click_max);
  const nothing = dry && !num(dry.models_total);

  return (
    <Modal title={job.label ? `ค้นใหม่ — ${job.label}` : title}
      sub="AI ค้นจากเว็บผู้ผลิต — ผลเป็นแค่ร่าง ยังไม่เขียนลงสินค้าจนกว่าคนจะกดยืนยัน" onClose={safeClose} wide>
      {notice && <div className="dossier-warn">{notice}</div>}

      {step === 'checking' && <div className="jr-empty">กำลังตรวจจำนวนรุ่นและค่าใช้จ่าย…</div>}

      {step === 'notready' && (
        <div className="placeholder men-ph">
          <h2>ยังไม่พร้อมใช้งาน</h2>
          <p>ระบบหาวัสดุด้วย AI ยังตั้งค่าไม่เสร็จ — ฝากแจ้งแอดมินให้ช่วยตั้งค่าให้เสร็จก่อน</p>
        </div>
      )}

      {step === 'error' && (
        <>
          <div className="ferr">{err}</div>
          <div className="mfoot">
            <button type="button" className="btn2" onClick={backFromConfirm}>{results ? '← กลับไปหน้าผล' : 'ปิด'}</button>
            <button type="button" className="btn1" onClick={() => setJob(j => ({ ...j }))}>ลองใหม่</button>
          </div>
        </>
      )}

      {(step === 'confirm' || step === 'sending') && dry && (
        <>
          <div className="men-grid">
            <div className="men-stat"><span>เลือกไว้กี่รายการ?</span><b>{num(dry.selected_rows).toLocaleString()}</b></div>
            <div className="men-stat"><span>ค้นทั้งหมดกี่รุ่น?</span><b>{num(dry.models_total).toLocaleString()}</b></div>
            <div className="men-stat"><span>รุ่นที่ต้องค้นใหม่?</span><b>{num(dry.models_new).toLocaleString()}</b></div>
            <div className="men-stat"><span>รวมทุกสีกี่ SKU?</span><b>{num(dry.skus_total).toLocaleString()}</b></div>
            <div className="men-stat"><span>เคยค้นแล้วกี่รุ่น?</span><b>{num(dry.models_existing).toLocaleString()}</b></div>
            <div className="men-stat"><span>มีงานค้างในระบบกี่รุ่น? (รอคิว · กำลังค้น · รอคนตรวจ)</span><b>{num(dry.models_active).toLocaleString()}</b></div>
            <div className="men-stat hl"><span>ค่าใช้จ่ายประมาณเท่าไร?</span><b>{fmtUsd(dry.est_usd_low)}–{fmtUsd(dry.est_usd_high)}</b></div>
            {dry.daily && <div className="men-stat"><span>ใช้ไปแล้วใน 24 ชม.?</span><b>{fmtUsd(dry.daily.spent_usd)} / {fmtUsd(dry.daily.max_usd)}</b></div>}
          </div>
          <div className="fhint men-why">ค้นทีละรุ่น — สี/ไซส์ของรุ่นเดียวกันใช้ผลเดียวกัน จึงรวมสีที่ไม่ได้ติ๊กด้วย ค่าใช้จ่ายเป็นตัวเลขประมาณการ</div>

          {!!dry.paused && (
            <div className="dossier-warn">{thaiOr(dry.paused_reason, 'ตอนนี้พักคิวอยู่')} — ลงคิวไว้ได้ แต่จะเริ่มค้นเมื่อเดินคิวต่อที่หน้า “วัสดุจาก AI”</div>
          )}
          {dry.daily && num(dry.daily.max_usd) > 0 && num(dry.daily.spent_usd) >= num(dry.daily.max_usd) && (
            <div className="dossier-warn">ยอดใช้ 24 ชม. ถึงเพดานแล้ว — ลงคิวไว้ได้ งานจะรอจนกว่ายอดจะลดลง</div>
          )}
          <SkipNotes res={dry}/>
          {!!dry.unregistered_brands?.length && (
            <div className="men-note">แบรนด์ที่ยังไม่มีในทะเบียน: {dry.unregistered_brands.join(' · ')} — ค้นได้ แต่ไม่มีคำแนะนำเฉพาะแบรนด์ อาจใช้เวลาและค่าใช้จ่ายมากกว่าปกติ</div>
          )}

          {err && !overCap && <div className="ferr">{err}</div>}
          {overCap && (
            <div className="ferr">ครั้งนี้มีรุ่นที่ต้องค้นใหม่ {num(dry.models_new).toLocaleString()} รุ่น เกินเพดาน {num(dry.per_click_max).toLocaleString()} รุ่นต่อครั้ง
              — ฝากช่วยเลือกให้น้อยลงแล้วลองใหม่ (เช่น ทีละหมวด) · แอดมินปรับเพดานได้ที่หน้า “วัสดุจาก AI”</div>
          )}
          {overCap && beforeDone.current && <div className="men-note">ผลเดิมถูกบันทึกว่า “ไม่ใช้” ไปแล้ว — ปิดหน้าต่างนี้แล้วกด ↻ ค้นใหม่ ที่แถวเดิมได้เมื่อพร้อม</div>}
          {nothing && !overCap && <div className="men-note">ไม่มีรายการที่ค้นได้ในชุดนี้</div>}
          {!overCap && !nothing && !num(dry.models_new) && (
            <div className="men-note">รอบนี้ไม่ต้องค้นใหม่ ไม่มีค่าใช้จ่าย — กดแล้วระบบจะเพิ่ม SKU ที่ยังไม่มีเข้างานเดิมของแต่ละรุ่น แล้วบอกผล (ยังไม่เขียนค่าลงสินค้า)</div>
          )}

          <div className="mfoot">
            <button type="button" className="btn2" disabled={step === 'sending'} onClick={backFromConfirm}>{results ? '← กลับไปหน้าผล' : beforeDone.current ? 'ปิดไว้ก่อน' : 'ยังไม่ลงคิว'}</button>
            {!overCap && !nothing && (
              <button type="button" className="btn1" disabled={step === 'sending'} onClick={send}>
                {step === 'sending' ? 'กำลังลงคิว…' : beforeDone.current ? 'ลองลงคิวอีกครั้ง'
                  : num(dry.models_new) ? `ลงคิว ${num(dry.models_new).toLocaleString()} รุ่น` : 'เพิ่ม SKU เข้างานเดิมแล้วดูผล'}
              </button>
            )}
          </div>
        </>
      )}

      {step === 'done' && (
        <>
          <div className="pp-ok">
            {num(summary?.queued)
              ? <>ลงคิวใหม่ {num(summary.queued).toLocaleString()} รุ่น — ใช้เวลาประมาณรุ่นละ 1–5 นาที ปิดหน้าต่างนี้ได้เลย ผลจะขึ้นที่หน้า “วัสดุจาก AI”</>
              : <>รอบนี้ไม่มีรุ่นที่ต้องค้นใหม่ — ดูผลเดิมต่อรุ่นด้านล่าง หรือที่หน้า “วัสดุจาก AI”</>}
          </div>
          <SkipNotes res={summary}/>
          {!results?.length && <div className="jr-empty">ไม่มีรุ่นที่ลงคิวในรอบนี้</div>}
          <div className="men-rows">
            {(results ?? []).map(r => {
              const key = rowKey(r);
              const s = rowSt[key] ?? {};
              const late = r.skus_not_in_result ?? [];
              const exists = r.action === 'exists';
              // มีผลรอคนตรวจ = งานรอคนนี้อยู่ ไม่ใช่รอระบบ (เดิมขึ้น "กำลังค้นอยู่" แล้วคนนั่งรอเปล่า ๆ)
              const waitingReview = r.action === 'pending' && r.status === 'draft';
              return (
                <div className="men-row" key={key}>
                  <div className="men-row-h">
                    <span className="men-model">{r.model}</span>
                    <span className="men-brand">{r.brand}</span>
                    <span className={`hschip ${r.action === 'queued' ? 'ok' : exists || waitingReview ? 'miss' : ''}`}>
                      {exists ? `เคยค้นแล้ว · ${STATUS_LABEL[r.status] ?? 'มีผลเดิม'}` : actionText(r)}
                    </span>
                  </div>
                  {exists && !!late.length && r.status === 'rejected' && (
                    <div className="men-late">SKU ที่เลือกมา: <SkuList list={late}/> — ผลเดิมของรุ่นนี้ถูกบอกว่าไม่ใช้ จึงไม่มีผลให้ใช้ต่อ · ถ้าจะหาให้ SKU พวกนี้ ฝากกด ↻ ค้นใหม่ หรือพิมพ์ค่าเองที่หน้าแก้ไขสินค้า</div>
                  )}
                  {exists && !!late.length && r.status !== 'rejected' && (
                    <div className="men-late">SKU ที่ยังไม่ได้ผลนี้: <SkuList list={late}/> — ยังไม่เขียนลงสินค้า {r.status === 'not_found' ? 'พิมพ์ค่าที่หน้าตรวจผลก่อน แล้วจึงเลือกทีละ SKU ได้ เห็นค่าเดิมก่อนกด' : 'เลือกทีละ SKU ได้ที่หน้าตรวจผล เห็นค่าเดิมก่อนกด'}</div>
                  )}
                  {(exists || waitingReview) && (
                    <div className="men-acts">
                      {r.research_id && (
                        <a className="btn1 sm men-link" href={routeUrl({ tab: 'materials', focus: r.research_id })}
                          aria-disabled={!!s.busy} onClick={(e) => openRow(e, r.research_id)}>{reviewLinkText(r, late.length)}</a>
                      )}
                      {exists && <button type="button" className="btn2 sm" disabled={!!s.busy} onClick={() => researchAgain(r)}>{s.busy ? 'กำลังเตรียม…' : '↻ ค้นใหม่'}</button>}
                    </div>
                  )}
                  {s.msg && <div className="ferr">{s.msg}</div>}
                </div>
              );
            })}
          </div>
          <div className="mfoot">
            <button type="button" className="btn2" disabled={rowBusy} onClick={safeClose}>ปิด</button>
            {onOpenReview && !onReviewPage && <button type="button" className="btn1" disabled={rowBusy} onClick={() => { onClose(); onOpenReview(); }}>ไปหน้าตรวจผล →</button>}
          </div>
        </>
      )}

      {(step === 'notready' || step === 'checking') && (
        <div className="mfoot"><button type="button" className="btn2" onClick={safeClose}>ปิด</button></div>
      )}
    </Modal>
  );
}

// ปุ่ม "หาวัสดุที่ยังว่าง" — ใช้ร่วมกันทั้ง Console (ทั้งตู้) และใบข้อมูลสินค้า (ใบเดียว)
//   โหลดรายการตอนกด ไม่ใช่ตอนเปิดหน้า · บรรทัดที่ไม่มีใบสินค้าค้นให้ไม่ได้ → บอกจำนวน + ชี้ไปสร้างใบ (ไม่ข้ามเงียบ ๆ)
//   onMessage(text) = ข้อความที่ไม่ต้องเปิดหน้าต่าง (ไม่มีอะไรให้ค้น / โหลดไม่สำเร็จ)
export function MaterialGapButton({ consignmentIds, docDate = null, className = 'btn2 sm', label = 'หาวัสดุที่ยังว่าง', title, onMessage, onOpenReview, style }) {
  const [busy, setBusy] = useState(false);
  const [gap, setGap] = useState(null);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  const noCardText = (n) => `อีก ${n} รายการยังไม่มีใบข้อมูลสินค้า ค้นวัสดุให้ไม่ได้ — ฝากช่วยกด “สร้างใบข้อมูลสินค้า” ในใบข้อมูลสินค้าของ supplier นั้นก่อน`;
  const open = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const g = await db.materialGapIds(consignmentIds);
      if (!alive.current) return;
      if (g.ids.length) setGap(g);
      else if (g.noCard) onMessage?.(noCardText(g.noCard));
      else onMessage?.(g.filled ? 'ทุกรายการในใบนี้มีวัสดุแล้ว ไม่มีอะไรให้ค้นเพิ่ม' : 'ยังไม่มีรายการสินค้าในใบนี้');
    } catch (e) {
      if (alive.current) onMessage?.(e?.message ?? 'โหลดรายการสินค้าไม่สำเร็จ — ฝากลองใหม่อีกครั้ง');
    }
    if (alive.current) setBusy(false);
  };

  return (
    <>
      <button type="button" className={className} style={style} disabled={busy} title={title} onClick={open}>
        {busy ? 'กำลังโหลด…' : label}
      </button>
      {gap && (
        <MaterialEnqueueModal ids={gap.ids} docDate={docDate} title="หาวัสดุที่ยังว่าง"
          notice={gap.noCard ? noCardText(gap.noCard) : null}
          onClose={() => setGap(null)} onOpenReview={onOpenReview}/>
      )}
    </>
  );
}
